// Curvas de crecimiento de los controles de niño para el PDF firmado (`DatosCurvasCrecimientoPdf` de @logiacare/hce-pdf): la referencia que ya se ve
// en pantalla (`SignosVitalesTab`) más las mediciones del paciente (aunque sea una sola, que la curva de pantalla exige de a dos). Puro, sin React.
//
// Tres familias, con las mismas gráficas que la pantalla:
//  - `fenton`  — 016B (niños), 016C (niñas) y 028A-1: Peso, Longitud y Perímetro cefálico contra edad postmenstrual, en SEMANAS (22 a 50).
//  - `oms0a5`  — 028B-1/028B-2: Peso/Edad, Talla/Edad y Perímetro cefálico/Edad OMS, en MESES (0 a 60; edad corregida si nació prematuro), más Peso para la
//                longitud (0-2 años, eje X = longitud en CM), Peso para la talla (2-5 años, eje X = talla en CM) e IMC/Edad OMS (0 a 60 meses): 6 gráficas, el tope del servidor.
//  - `oms5a19` — 028C-1/028C-2: Talla/Edad e IMC/Edad OMS 2007 (5 a 19 años) y Peso/Edad OMS (5 a 10 años, tope oficial de la OMS), en AÑOS.
// Limitación conocida: en Fenton `girls` es hoy copia de `boys` (016C y las niñas de 028A-1 se dibujan con la referencia de niños) porque la Universidad de
// Calgary no publica la tabla numérica de Fenton 2013 (solo por pedido al autor): por eso la nota de esas gráficas lleva el aviso `avisoFentonNinas`.
import type { Observation, Patient } from '@medplum/fhirtypes';
import type { DatosCurvasCrecimientoPdf, GraficaCurvaPdf, PuntoCurvaPdf, SerieCurvaPdf } from '@logiacare/hce-pdf';
import type { PuntoSerie } from '../graficos/tipos';
import {
  CONFIG_PC_EDAD_0A5,
  CONFIG_PESO_EDAD_0A5,
  CONFIG_PESO_EDAD_5A10,
  CONFIG_TALLA_EDAD_0A5,
  CONFIG_TALLA_EDAD_5A19,
  PERIMETRO_CEFALICO_LOINC,
  PESO_LOINC,
  TALLA_LOINC,
  lineasReferenciaControlNino,
  puntosFenton,
  puntosImcEdad,
  puntosImcEdad0a5,
  puntosPacienteCurva,
  puntosPesoLongitud,
  puntosPesoTalla,
  type ConfigCurvaPercentil,
  type ReferenciaControlNino,
} from './curvaPercentilPorEdad';

export type FamiliaCurvasControlNino = 'fenton' | 'oms0a5' | 'oms5a19';

/** Identificador de cada gráfica; `talla` es la LONGITUD en Fenton. */
export type IdGraficaControlNino = 'peso' | 'talla' | 'perimetroCefalico' | 'pesoLongitud' | 'pesoTalla' | 'imc';

export interface TextoGraficaPdf {
  readonly titulo: string;
  readonly nota: string;
  /** Rótulo del eje X propio de la gráfica, si difiere del de la familia (peso para la longitud/talla: el eje X es la longitud/talla en cm, no la edad). */
  readonly ejeX?: string;
}

/** Gráficas de cada familia, en el orden en que salen en el PDF. */
export const GRAFICAS_POR_FAMILIA = {
  fenton: ['peso', 'talla', 'perimetroCefalico'],
  oms0a5: ['peso', 'talla', 'perimetroCefalico', 'pesoLongitud', 'pesoTalla', 'imc'],
  oms5a19: ['talla', 'peso', 'imc'],
} as const satisfies Record<FamiliaCurvasControlNino, readonly IdGraficaControlNino[]>;

/** Gráficas cuyo eje X no es la edad de la familia sino la longitud/talla en cm: su texto trae `ejeX` propio (ver `TextoGraficaPdf`). */
export const GRAFICAS_CON_EJE_X_PROPIO: ReadonlySet<IdGraficaControlNino> = new Set<IdGraficaControlNino>(['pesoLongitud', 'pesoTalla']);

export type IdsGraficasDe<F extends FamiliaCurvasControlNino> = (typeof GRAFICAS_POR_FAMILIA)[F][number];

export interface TextosCurvasControlNinoPdf<F extends FamiliaCurvasControlNino> {
  readonly graficas: Readonly<Record<IdsGraficasDe<F>, TextoGraficaPdf>>;
  readonly ejeX: string;
  /** Solo Fenton: aviso que se agrega a la nota de cada gráfica de una NIÑA mientras `girls` sea copia de `boys` (ver `FENTON_NINAS_USA_REFERENCIA_DE_NINOS`). */
  readonly avisoFentonNinas?: string;
}

/**
 * Hoy `girls` == `boys` en los tres datasets de Fenton (`graficos/datos/*Fenton*.json`): las niñas se dibujan con la referencia de niños. Un PDF firmado
 * no puede callarlo, así que la nota de esas gráficas lo dice. Cuando otra tarea cargue la referencia de niñas, poner en `false` (y quitar el aviso).
 */
export const FENTON_NINAS_USA_REFERENCIA_DE_NINOS = true;

const DIAS_POR_SEMANA = 7;
const DIAS_POR_MES = 365.25 / 12;
const DIAS_POR_ANIO = 365.25;

/** El servidor rechaza más de 80 puntos por serie: del paciente se conservan los últimos (los más recientes). */
const TOPE_PUNTOS_PACIENTE = 80;
/** Máximos plausibles por magnitud: por encima es un error de captura (p. ej. talla 1500 por un decimal olvidado) y no se dibuja ni estira el eje. El servidor acota los ejes a |v| ≤ 1e6. */
const MAXIMO_PESO_KG = 250;
const MAXIMO_LONGITUD_CM = 250;
const MAXIMO_PERIMETRO_CM = 100;
const MAXIMO_IMC = 100;

interface EspecGrafica {
  readonly referencia: ReferenciaControlNino;
  readonly etiquetaPaciente: string;
  readonly ejeY: string;
  /** Paso al que se redondea el eje Y hacia afuera. */
  readonly pasoY: number;
  /** Valor máximo plausible de la magnitud (ver `MAXIMO_*`). */
  readonly maximo: number;
  /** Fin del eje X (en la unidad de la familia) si difiere del de la familia: Peso/Edad OMS termina a los 10 años. */
  readonly xMax?: number;
  /** Eje X propio, en CM (no en la unidad de edad de la familia): peso para la longitud/talla. Los puntos (referencia y paciente) ya vienen en cm. */
  readonly ejeCm?: { readonly xMin: number; readonly xMax: number; readonly pasoX: number };
  readonly puntos: (observaciones: readonly Observation[], paciente: Pick<Patient, 'birthDate' | 'gender'>) => PuntoSerie[];
}

interface EspecFamilia {
  readonly diasPorUnidad: number;
  readonly xMin: number;
  readonly xMax: number;
  readonly pasoX: number;
  readonly graficas: Readonly<Record<IdGraficaControlNino, EspecGrafica | undefined>>;
}

const deConfig = (config: ConfigCurvaPercentil): EspecGrafica['puntos'] => (o, p) => puntosPacienteCurva(config, o, p);

const FAMILIAS: Record<FamiliaCurvasControlNino, EspecFamilia> = {
  fenton: {
    diasPorUnidad: DIAS_POR_SEMANA,
    xMin: 22,
    xMax: 50,
    pasoX: 2,
    graficas: {
      peso: { referencia: 'fenton-peso', maximo: MAXIMO_PESO_KG, etiquetaPaciente: 'Peso', ejeY: 'kg', pasoY: 1, puntos: (o, p) => puntosFenton(PESO_LOINC, o, p) },
      talla: { referencia: 'fenton-longitud', maximo: MAXIMO_LONGITUD_CM, etiquetaPaciente: 'Longitud', ejeY: 'cm', pasoY: 5, puntos: (o, p) => puntosFenton(TALLA_LOINC, o, p) },
      perimetroCefalico: { referencia: 'fenton-perimetro-cefalico', maximo: MAXIMO_PERIMETRO_CM, etiquetaPaciente: 'Perímetro cefálico', ejeY: 'cm', pasoY: 2, puntos: (o, p) => puntosFenton(PERIMETRO_CEFALICO_LOINC, o, p) },
      pesoLongitud: undefined,
      pesoTalla: undefined,
      imc: undefined,
    },
  },
  oms0a5: {
    diasPorUnidad: DIAS_POR_MES,
    xMin: 0,
    xMax: 60,
    pasoX: 6,
    graficas: {
      peso: { referencia: 'oms0a5-peso', maximo: MAXIMO_PESO_KG, etiquetaPaciente: 'Peso', ejeY: 'kg', pasoY: 2, puntos: deConfig(CONFIG_PESO_EDAD_0A5) },
      talla: { referencia: 'oms0a5-talla', maximo: MAXIMO_LONGITUD_CM, etiquetaPaciente: 'Talla', ejeY: 'cm', pasoY: 5, puntos: deConfig(CONFIG_TALLA_EDAD_0A5) },
      perimetroCefalico: { referencia: 'oms0a5-perimetro-cefalico', maximo: MAXIMO_PERIMETRO_CM, etiquetaPaciente: 'Perímetro cefálico', ejeY: 'cm', pasoY: 2, puntos: deConfig(CONFIG_PC_EDAD_0A5) },
      pesoLongitud: { referencia: 'oms0a5-peso-longitud', maximo: MAXIMO_PESO_KG, etiquetaPaciente: 'Peso', ejeY: 'kg', pasoY: 2, ejeCm: { xMin: 45, xMax: 110, pasoX: 5 }, puntos: puntosPesoLongitud },
      pesoTalla: { referencia: 'oms0a5-peso-talla', maximo: MAXIMO_PESO_KG, etiquetaPaciente: 'Peso', ejeY: 'kg', pasoY: 2, ejeCm: { xMin: 65, xMax: 120, pasoX: 5 }, puntos: puntosPesoTalla },
      imc: { referencia: 'oms0a5-imc', maximo: MAXIMO_IMC, etiquetaPaciente: 'IMC', ejeY: 'kg/m²', pasoY: 2, puntos: puntosImcEdad0a5 },
    },
  },
  oms5a19: {
    diasPorUnidad: DIAS_POR_ANIO,
    xMin: 5,
    xMax: 19,
    pasoX: 1,
    graficas: {
      peso: { referencia: 'oms5a19-peso', maximo: MAXIMO_PESO_KG, etiquetaPaciente: 'Peso', ejeY: 'kg', pasoY: 5, xMax: 10, puntos: deConfig(CONFIG_PESO_EDAD_5A10) },
      talla: { referencia: 'oms5a19-talla', maximo: MAXIMO_LONGITUD_CM, etiquetaPaciente: 'Talla', ejeY: 'cm', pasoY: 10, puntos: deConfig(CONFIG_TALLA_EDAD_5A19) },
      perimetroCefalico: undefined,
      pesoLongitud: undefined,
      pesoTalla: undefined,
      imc: { referencia: 'oms5a19-imc', maximo: MAXIMO_IMC, etiquetaPaciente: 'IMC', ejeY: 'kg/m²', pasoY: 2, puntos: (o, p) => puntosImcEdad(o, p) },
    },
  },
};

const redondeo = (n: number): number => Math.round(n * 100) / 100;

/** Ejes Y "redondos" (múltiplos de `paso`) que abarquen toda la referencia y las mediciones que caen dentro del eje X. */
function rangoY(series: readonly SerieCurvaPdf[], paso: number): { yMin: number; yMax: number } {
  const ys = series.flatMap((s) => s.puntos.map((p) => p.y));
  if (ys.length === 0) return { yMin: 0, yMax: paso };
  const yMin = Math.max(0, Math.floor(Math.min(...ys) / paso) * paso);
  const yMax = Math.ceil(Math.max(...ys) / paso) * paso;
  return { yMin, yMax: yMax > yMin ? yMax : yMin + paso };
}

/**
 * Las gráficas de la familia con su referencia (7 desviaciones estándar) y las mediciones del paciente; `undefined` si falta la fecha de nacimiento o el
 * sexo (sin ellos no hay eje de edad ni referencia). Las mediciones `entered-in-error`, fuera del eje X o con un valor imposible no cuentan.
 */
export function datosCurvasControlNinoPdf<F extends FamiliaCurvasControlNino>(
  familia: F,
  paciente: Pick<Patient, 'birthDate' | 'gender'>,
  observaciones: readonly Observation[],
  textos: TextosCurvasControlNinoPdf<F>,
): DatosCurvasCrecimientoPdf | undefined {
  if (!paciente.birthDate || (paciente.gender !== 'male' && paciente.gender !== 'female')) return undefined;
  const espec = FAMILIAS[familia];
  const vigentes = observaciones.filter((o) => o.status !== 'entered-in-error');

  const graficas: GraficaCurvaPdf[] = [];
  for (const id of GRAFICAS_POR_FAMILIA[familia] as readonly IdsGraficasDe<F>[]) {
    const g = espec.graficas[id];
    if (!g) continue;
    const xMin = g.ejeCm?.xMin ?? espec.xMin;
    const xMax = g.ejeCm?.xMax ?? g.xMax ?? espec.xMax;
    const pasoX = g.ejeCm?.pasoX ?? espec.pasoX;
    const xDe = (x: number): number => redondeo(g.ejeCm ? x : x / espec.diasPorUnidad);
    const referencia: SerieCurvaPdf[] = lineasReferenciaControlNino(paciente.gender, g.referencia).map((s) => ({
      etiqueta: s.etiqueta,
      estilo: 'referencia' as const,
      puntos: s.puntos.map((p): PuntoCurvaPdf => ({ x: xDe(p.x), y: redondeo(p.y) })).filter((p) => p.x >= xMin && p.x <= xMax),
    }));
    const delPaciente = g
      .puntos(vigentes, paciente)
      .map((p): PuntoCurvaPdf => ({ x: xDe(p.x), y: redondeo(p.y) }))
      .filter((p) => p.x >= xMin && p.x <= xMax && Number.isFinite(p.y) && p.y > 0 && p.y <= g.maximo)
      .slice(-TOPE_PUNTOS_PACIENTE);
    const series: SerieCurvaPdf[] = [...referencia.filter((s) => s.puntos.length >= 2), ...(delPaciente.length > 0 ? [{ etiqueta: g.etiquetaPaciente, estilo: 'paciente' as const, puntos: delPaciente }] : [])];
    const { yMin, yMax } = rangoY(series, g.pasoY);
    const texto = textos.graficas[id];
    const avisoNinas = familia === 'fenton' && paciente.gender === 'female' && FENTON_NINAS_USA_REFERENCIA_DE_NINOS ? textos.avisoFentonNinas : undefined;
    graficas.push({ titulo: texto.titulo, nota: avisoNinas ? `${texto.nota} ${avisoNinas}` : texto.nota, ejeXEtiqueta: texto.ejeX ?? textos.ejeX, ejeYEtiqueta: g.ejeY, xMin, xMax, yMin, yMax, pasoX, series });
  }
  return { graficas };
}
